/**
 * Active-program derivation (P7.1) — the locked mockup's navigation model:
 * one program visible at a time. Program routes derive it from the path;
 * everywhere else (Home, Operations, Admin, …) keeps the session program so
 * the sidebar does not flip to the Programs launcher (owner 2026-07-21).
 * The Programs launcher only appears when no program is selected yet (or the
 * user clicked "Choose a different program" and cleared the session).
 * (docs/reference/app-plus/UI_ALIGNMENT.md §1)
 */

export type ProgramContext = 'mips' | 'app-plus' | 'neutral';

const MIPS_PREFIXES = [
  '/dashboard',
  '/programs/mips',
  '/measure-selection',
  '/gaps',
  '/scorecards',
  '/insights',
  '/submit',
];

/** Fired when sessionStorage.homeProgram is written/cleared so React can re-render. */
export const HOME_PROGRAM_EVENT = 'vbca:homeProgram';

/** Pure route classifier — unit-tested; keep free of React / sessionStorage. */
export function programContextForPath(pathname: string): ProgramContext {
  if (pathname.startsWith('/programs/app-plus')) return 'app-plus';
  if (MIPS_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'))) return 'mips';
  return 'neutral';
}

/**
 * Sidebar / top-bar program for the current screen.
 * - Program routes → that program (path wins).
 * - Any other route → session program when set (keeps APP Plus / MIPS nav
 *   visible on Home + Operations).
 * - No session → 'neutral' (Programs launcher).
 *
 * Pure: pass sessionProgram from the caller (sessionStorage.homeProgram).
 */
export function resolveActiveProgram(
  pathname: string,
  sessionProgram: string | null | undefined,
): ProgramContext {
  const fromPath = programContextForPath(pathname);
  if (fromPath !== 'neutral') return fromPath;
  if (sessionProgram === 'mips' || sessionProgram === 'app-plus') {
    return sessionProgram;
  }
  return 'neutral';
}

/** Write/clear the session program and notify sidebar / top-bar listeners. */
export function setHomeProgram(program: 'mips' | 'app-plus' | null): void {
  if (program) sessionStorage.setItem('homeProgram', program);
  else sessionStorage.removeItem('homeProgram');
  window.dispatchEvent(new Event(HOME_PROGRAM_EVENT));
}

export function getHomeProgram(): string | null {
  return sessionStorage.getItem('homeProgram');
}
